import { useState, useEffect } from "react";
import { useAuth } from "../../../provider";
import { Formik, Form } from 'formik';
import { useNavigate, useLocation } from "react-router-dom";
import * as Yup from 'yup';
import FormAlert from "../../../components/Alert/FormAlert";
import ButtonForm from "../../../components/Button/ButtonForm";
import DateInput from "../../../components/Forms/DateInput";
import TimeInput from "../../../components/Forms/TimeInput";
import SelectInputNew from "../../../components/Forms/SelectInputNew";
import * as c from '../../../constants';
import axios from 'axios';
import { Checkbox, FormGroup, FormControlLabel, Paper, Typography, Grid } from "@mui/material";



export default function RescheduleForm() {
  const { state } = useAuth();
  const navigate = useNavigate();

  const location = useLocation();
  const from = location.state?.from?.pathname || "/WaitingResults";

  const [visits, setVisits] = useState([]);
  const [visitId, setVisitId] = useState(null);
  const [initialValues, setInitialValues] = useState({});
  const [labOptions, setLabOptions] = useState([{ value: "", label: "Loading labs..." }]);

  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState(null);

  // 🔹 Fetch user's current lab visit
  useEffect(() => {
    const fetchVisits = async () => {
      try {
        const res = await axios.get(
          `${c.API_URL}/api/current-lab-visit/`
        );
        const data = res.data;
        const visitsArray = Array.isArray(data) ? data : data ? [data] : [];
        setVisits(visitsArray);

        if (data) {
          setVisitId(data.id);
          setInitialValues({
            laboratory: data.laboratory?.id || "",
            visit_date: data.visit_date || "",
            visit_time: data.visit_time || "",
          });
        }
      } catch (err) {
        console.error("No existing lab visit found:", err);
      }
    };

    if (state?.user?.id) fetchVisits();
  }, [state.user?.id]);

  // 🔹 Fetch available labs
  useEffect(() => {
    const fetchLabs = async () => {
      try {
        const res = await axios.get(`${c.API_URL}/api/laboratories/`);
        const labs = Array.isArray(res.data)
          ? res.data
          : res.data.results || [];
        const options = labs.map((lab) => ({
          value: lab.id,
          label: `${lab.name}`,
        }));
        setLabOptions([{ value: "", label: "Select a Lab" }, ...options]);
      } catch (err) {
        console.error("Failed to fetch labs:", err);
        setLabOptions([{ value: "", label: "Error loading labs" }]);
      }
    };
    fetchLabs();
  }, []);

  // 🔹 Select or unselect a visit
  const handleSelectVisit = (visit) => {
    if (visitId === visit.id) {
      setVisitId(null);
      setInitialValues({});
      return;
    }

    setVisitId(visit.id);
    setInitialValues({
      laboratory: visit.laboratory?.id || "", // ✅ store lab ID
      visit_date: visit.visit_date,
      visit_time: visit.visit_time,
    });
    setSuccessMessage("");
  };

  // 🔹 Submit handler
  const handleSubmit = async (values, formikHelpers) => {
    setLoading(true);
    setErrorMessage(null);
    setSuccessMessage("");

    const payload = {
      laboratory_id: parseInt(values.laboratory),
      visit_date: values.visit_date,
      visit_time: values.visit_time,
      user: state.user.id,
      user_first_name: state.user.first_name,
      user_last_name: state.user.last_name,
      user_email: state.user.email,
      user_gender: state.user.gender,
      user_date_of_birth: state.user.date_of_birth,
    };

    try {
      const response = visitId
        ? await axios.put(`${c.API_URL}/api/lab-visits/${visitId}/`, payload)
        : await axios.post(`${c.API_URL}/api/lab-visits/`, payload);

      if ([200, 201].includes(response.status)) {
        setSuccessMessage("Lab visit updated successfully!");
        // 🔁 Redirect to summary to reflect the change
        navigate("/WaitingResultsSummary", { replace: true });
      }
    } catch (error) {
      console.error(error);
      setErrorMessage({ message: "Server error. Please try again later." });
    } finally {
      setLoading(false);
      formikHelpers.setSubmitting(false);
    }
  };

  return (
 <>
      <Grid container spacing={2} sx={{ mx: 2, p: 2, display: "flex", justifyContent: "center" }}>
        {/* 🔹 Existing visits list */}
        <Grid size={12} sx={{ maxWidth: 600 }}>
          <Typography variant="h6" textAlign="center" sx={{ mb: 2 }}>
            Select the scheduled lab visit you want to change:
          </Typography>

          <FormGroup
            row
            sx={{
              display: "flex",
              flexDirection: "row",
              flexWrap: "wrap",
              justifyContent: "center",
              gap: 2,
            }}
          >
            {Array.isArray(visits) && visits.length > 0 ? (
              visits.map((visit) => (
                <Paper
                  key={visit.id}
                  elevation={2}
                  sx={{
                    px: 4,
                    py: 2,
                    mb: 2,
                    borderRadius: 1,
                    minWidth: 280,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    transition: "0.3s",
                    backgroundColor:
                      visitId === visit.id ? 'grey.C300' : 'grey.C200',
                  }}
                >
                  <div>
                    <Typography variant="subtitle2" fontWeight={500}>
                      {`${visit.laboratory?.name}`}
                    </Typography>
                    <Typography variant="subtitle2" fontWeight={500}>
                      Date: {visit.visit_date}
                    </Typography>
                    <Typography variant="subtitle2" fontWeight={500}>
                      Time: {visit.visit_time}
                    </Typography>
                    <Typography variant="subtitle2" fontWeight={500}>
                      Code: {visit.check_in_code}
                    </Typography>
                  </div>

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={visitId === visit.id}
                        onChange={() => handleSelectVisit(visit)}
                        color="warning"
                        size="small"
                      />
                    }
                    label="Select"
                  />
                </Paper>
              ))
            ) : (
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", mt: 1 }}
              >
                No scheduled lab visits found.
              </Typography>
            )}
          </FormGroup>
        </Grid>

        {/* 🔹 Reschedule form */}
        <Grid size={12} sx={{ maxWidth: 600, display:'flex', justifyContent:'center'}}>
          <Typography variant="h6" textAlign="center" sx={{ mt: 3 }}>
            Change lab, date, time - if required
          </Typography>
        </Grid>
        <Grid size={12} sx={{mb:4, maxWidth: 600, display:'flex', justifyContent:'center'}}>

          <Formik
            enableReinitialize
            initialValues={{
              laboratory: initialValues.laboratory || "",
              visit_date: initialValues.visit_date || "",
              visit_time: initialValues.visit_time || "",
            }}
            validationSchema={validationLabDate}
            onSubmit={handleSubmit}
          >
            {({ isSubmitting }) => (
              <Form>
                <SelectInputNew label="Select a Lab" name="laboratory" options={labOptions} />
                <DateInput label="Pick new date" name="visit_date" />
                <TimeInput label="Pick new time" name="visit_time" />
                <ButtonForm name="Submit Data" disabled={isSubmitting || loading} />
              </Form>
            )}
          </Formik>
        </Grid>
 </Grid>
        {/* 🔹 Alerts */}
        {errorMessage && (
          <FormAlert
            severity="error"
            title="Form Submission Error"
            message={errorMessage.message}
            onClose={() => setErrorMessage(null)}
            sx={{ mt: 2 }}
          />
        )}

        {successMessage && (
          <FormAlert
            severity="success"
            title="Success"
            message={successMessage}
            onClose={() => setSuccessMessage("")}
            sx={{ mt: 2 }}
          />
        )}
     
    </>
  );
}

// ✅ Yup validation
const validationLabDate = Yup.object({
  laboratory: Yup.string().required("Required"),
  visit_date: Yup.string()
    .required("Required")
    .matches(/^\d{4}-\d{2}-\d{2}$/, "Invalid date format")
    .test("is-future-date", "Date cannot be in the past", (value) => {
      if (!value) return false;
      const today = new Date();
      const selected = new Date(value);
      return selected >= today;
    }),
});
